import { useParams } from 'react-router-dom';
import { useIsOnline } from '../../../hooks';
import { useGetOfflineFirstSectionsQuery, useGetSectionsQuery } from '../../../services/api/vehicle-sections';
import { useCallback, useEffect, useState } from 'react';
import { db, SectionRow } from '../../../db';
import { Section } from '../../../services/api/vehicle-sections/types';
import { setIn } from 'final-form';
import { generateRandomId } from '../../../utils/crypto';
import { useGetVehicleByIdQuery } from 'services/api/upload-vehicle';
import { getAuctionTypeInitialValue, serializeUploadInitialValues } from 'utils/uploadVehicle';
import { useIsSellerLocked } from 'services/sellerLock/sellerLockSlice';
import { useCurrentUser } from 'hooks/selectors';
import { useGetSellersQuery } from 'services/api';

async function buildTree(
  vehicleId: number,
  parentSection: SectionRow | null,
  parentKey: string | null,
  result: Record<string, Section>
) {
  const sections = await db.sections
    .where('[formId+parentId]')
    .equals([vehicleId, parentSection?.id ?? 0])
    .sortBy('order');

  for (const section of sections) {
    const key = (() => {
      if (!parentSection || !parentKey) return section.name;
      return `${parentKey}.subsections.${section.name}`;
    })();
    console.log(key, 'key');
    const copy = { ...section } as Partial<SectionRow & Section>;
    delete copy.formId;
    if (section.parentId === 0) {
      copy.parentId = null;
    }

    const images = await db.images
      .where('sectionId')
      .equals(section.id)
      .toArray();

    copy.images = images.map<any>(img => ({
      id: img.serverId!,
      vehicle_section_id: img.sectionId,
      file: img.blob ? new File([img.blob], generateRandomId()) : undefined,
      url: img.url!,
      url_thumb: img.url_thumb,
      notes: img.notes || undefined,
      o: img.o
    }));

    const checkboxes = await db.checkboxes
      .where('sectionId')
      .equals(section.id)
      .toArray();

    copy.checkboxes = checkboxes as any[];

    const updatedResult = setIn(result, key, copy) as Record<string, Section>;
    result = await buildTree(vehicleId, section, key, updatedResult);
  }

  return result;
}

export const useInitialFormValues = () => {
  const { id: vehicleId } = useParams<{ id: string }>();
  const { data: vehicle } = useGetVehicleByIdQuery(Number(vehicleId), { skip: !vehicleId });
  const { data } = useGetOfflineFirstSectionsQuery(Number(vehicleId), { skip: !vehicleId });

  const sections = data?.data;

  const [initialValues, setInitialValues] = useState<any>({});

  const vehicleValues = useInitialVehicle();

  const getInitialValues = useCallback(async () => {
    const numericId = Number(vehicleId);
    if (!numericId) return;

    if (!sections) return;
    setInitialValues({
      ...sections,
      ...vehicleValues,
      vehicleId: numericId,
    });
  }, [sections, vehicleId, vehicle?.inspector_notes, vehicle]);

  useEffect(() => {
    getInitialValues();
  }, [getInitialValues]);

  return initialValues;
};

const useInitialVehicle = () => {
  const { id: vehicleId } = useParams<{ id: string }>();
  const { data: vehicle } = useGetVehicleByIdQuery(Number(vehicleId), { skip: !vehicleId });

  const user = useCurrentUser();
  const isSellerLocked = useIsSellerLocked();

  const { data: users = [] } = useGetSellersQuery(undefined, {
    skip: user.role !== 'admin'
  });

  const userOptions = users.map(u => ({
    value: u.id,
    label: u.dealership_name
  }));

  const initialSellerId = (() => {
    if (user.role === 'seller') {
      return user.id;
    }

    if (vehicle) {
      return userOptions.find(e => e.value === vehicle.user_id);
    }

    if (isSellerLocked) {
      const sellerId = localStorage.getItem('locked_seller');
      if (!sellerId) return null;
      return userOptions.find(e => e.value === Number(sellerId));
    }

    return null;
  })();

  if (!vehicle) return {};

  return {
    ...serializeUploadInitialValues(vehicle),
    vin: vehicle?.vin,
    auction_type: getAuctionTypeInitialValue(vehicle),
    upload_type: vehicle.upload_type,
    seller_id: initialSellerId,
    is_guaranteed: vehicle?.is_guaranteed,
    inspector_notes: vehicle?.inspector_notes || {}
  };
};
